import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";

import { requiredValidator } from "../../components/formFields/CommonValidator";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { LoadingPanel } from "../../components/layout/Loading";

import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY, VISA_TYPE } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { RxCross1 } from "react-icons/rx";
import {
  createVisaGateway,
  getAllVisaGateway,
  getVisaGatewayByID,
  updateVisaGateway,
  VisaGatewayImageUpload,
} from "./services/visagateway.services";
import { clearVisaGatewayDetails } from "./visagatewaySlice";
import { getAllActiveCountries } from "../country/services/country.services";
import { ICountry } from "../country/countryModel";

const CreateVisaGateway: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const VisaGatewayID = useAppSelector((state) => state.drawer.data);

  const [formKey, setFormKey] = useState(1);
  const loading = useAppSelector((state) => state.visagateway.loading);
  const formLoading = useAppSelector((state) => state.visagateway.formLoading);
  const VisaGatewayDetails = useAppSelector(
    (state) => state.visagateway.VisaGatewayDetail
  );
  const CountryList = useAppSelector((state) => state.country.CountryList);

  useEffect(() => {
    dispatch(getAllActiveCountries());
    if (VisaGatewayID) {
      dispatch(getVisaGatewayByID(VisaGatewayID));
    }
  }, [VisaGatewayID]);

  useEffect(() => {
    return () => {
      dispatch(clearVisaGatewayDetails());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VisaGatewayDetails]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.Image && typeof values?.Image !== "string"
        ? values.Image[0].getRawFile()
        : "";

    try {
      if (VisaGatewayID) {
        const editpayload = {
          ID: VisaGatewayID,
          VisaType: values?.VisaType ? values?.VisaType : "",
          CountryID: values?.CountryID ? +values?.CountryID : null,
          IsActive: values?.IsActive === 1,
        };

        const response = await dispatch(updateVisaGateway(editpayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values.Image && typeof values.Image !== "string") {
            ImageData.append("Image", file);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(VisaGatewayImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(getAllVisaGateway());
        }
      } else {
        const insertpayload = {
          VisaType: values?.VisaType ? values?.VisaType : "",
          CountryID: values?.CountryID ? +values?.CountryID : null,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createVisaGateway(insertpayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", file);
            ImageData.append("ID", response?.payload?.Data?.ID);

            await dispatch(VisaGatewayImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(getAllVisaGateway());
        }
      }
    } catch (error) {
      console.log("error in hanldle submit", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {(loading || formLoading) && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={VisaGatewayDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {VisaGatewayID ? "Update Visa Gateway" : "Add Visa Gateway"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  width: "100%",
                }}
              >
                <Field
                  id={"CountryID"}
                  name={"CountryID"}
                  label={"Country"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={CountryList?.map((vcounry: ICountry) => {
                    return {
                      value: vcounry.ID,
                      label: vcounry.CountryName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  width: "100%",
                }}
              >
                <Field
                  name="VisaType"
                  label="Visa Type"
                  astrike={true}
                  placeholder="i.e. Visa Free"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={VISA_TYPE?.map((type: any) => {
                    return {
                      value: type?.value,
                      label: type?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  width: "100%",
                }}
              >
                <Field
                  name="IsActive"
                  label="Status"
                  astrike={true}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  width: "100%",
                }}
              >
                <Field
                  name="Image"
                  type="file"
                  accept="image/*"
                  label="Facility Image"
                  component={FormImageUpload}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  width: "100%",
                  marginTop: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                >
                  <ButtonWithLoading
                    // tabIndex={12}
                    label={VisaGatewayID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    // tabIndex={13}
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={handleFormClose}
                  >
                    Cancel
                  </Button>
                </div>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVisaGateway;
